/**
 * AttributeSelectionDialog
 * Shows when adding a lead/client to CRM — lets users select custom attribute values
 * Optional: user can skip with "Add Later" or select values and confirm
 */

import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { customAttributeApi } from '../api/customAttributeApi';
import type { CustomAttribute } from '../api/customAttributeApi';

interface AttributeSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (attributes: Record<string, string>) => void;
  loading?: boolean;
  entityName?: string; // e.g. "Acme Corp"
  appliesTo: 'LEAD' | 'CLIENT';
}

const AttributeSelectionDialog: React.FC<AttributeSelectionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  entityName,
  appliesTo,
}) => {
  const [selected, setSelected] = useState<Record<string, string>>({});

  const { data: attributes = [], isLoading } = useQuery({
    queryKey: ['customAttributes', appliesTo],
    queryFn: () => customAttributeApi.getAll(appliesTo),
    enabled: open,
  });

  const activeAttributes = attributes.filter(a => a.isActive && a.options.length > 0);

  const toggleOption = (attrId: string, optionValue: string) => {
    setSelected(prev => {
      if (prev[attrId] === optionValue) {
        const next = { ...prev };
        delete next[attrId];
        return next;
      }
      return { ...prev, [attrId]: optionValue };
    });
  };

  const handleConfirm = () => {
    onConfirm(selected);
    setSelected({});
  };

  const handleSkip = () => {
    onConfirm({});
    setSelected({});
  };

  const hasRequired = activeAttributes.some(a => a.isRequired);
  const requiredMissing = activeAttributes
    .filter(a => a.isRequired)
    .some(a => !selected[a.id]);

  if (!open) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Categorize{entityName ? `: ${entityName}` : ' this lead'}
      </DialogTitle>
      <DialogContent>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : activeAttributes.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No custom attributes defined yet. You can set them up in Settings &gt; Attributes.
          </Typography>
        ) : (
          <Box sx={{ mt: 1 }}>
            {activeAttributes.map((attr: CustomAttribute) => (
              <Box key={attr.id} sx={{ mb: 2.5 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {attr.name}
                  {attr.isRequired && (
                    <Typography component="span" variant="caption" color="error">*</Typography>
                  )}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  {attr.options.map(opt => {
                    const isSelected = selected[attr.id] === opt.value;
                    return (
                      <Chip
                        key={opt.id}
                        label={opt.label}
                        size="small"
                        onClick={() => toggleOption(attr.id, opt.value)}
                        sx={{
                          cursor: 'pointer',
                          borderColor: isSelected ? (opt.color || 'primary.main') : 'divider',
                          bgcolor: isSelected ? `${opt.color || '#1677ff'}15` : 'transparent',
                          color: isSelected ? (opt.color || 'primary.main') : 'text.primary',
                          fontWeight: isSelected ? 600 : 400,
                          '&:hover': {
                            bgcolor: isSelected ? `${opt.color || '#1677ff'}25` : 'action.hover',
                          },
                        }}
                        variant="outlined"
                      />
                    );
                  })}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        {activeAttributes.length > 0 && (
          <Button onClick={handleSkip} color="inherit">
            Add Later
          </Button>
        )}
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || (hasRequired && requiredMissing)}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {loading ? 'Adding...' : 'Add to CRM'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AttributeSelectionDialog;
